/**
 * Final step of the Google sign-in flow.
 *
 * The backend's ``/auth/google/callback`` redirects the browser here
 * with either ``?exchange=<code>`` or ``?error=<code>``. On
 * ``exchange`` we POST it to ``/auth/google/exchange`` to receive the
 * standard ``AuthResponse`` and adopt it through ``AuthProvider``;
 * on ``error`` we render a code-specific message with a "Back to
 * login" action that returns the user to ``/``.
 */

import { useEffect, useRef, useState } from "react";

import { ApiError } from "../api/client";
import { googleApi } from "../api/google";
import { useT } from "../lib/i18n";
import { useApi, useAuth } from "./AuthProvider";
import { AuthShell } from "./AuthShell";

type Phase = "loading" | "error";

interface GoogleFinishPageProps {
  /** Override for tests; production reads from ``window.location``. */
  search?: string;
  /** Override for tests; production calls ``window.location.replace``. */
  navigate?: (url: string) => void;
}

const ERROR_KEYS_BY_CODE: Readonly<Record<string, string>> = {
  cancelled: "googleErrorCancelled",
  already_linked: "googleErrorAlreadyLinked",
  email_unverified: "googleErrorEmailUnverified",
  provider_error: "googleErrorProvider",
  id_token_invalid: "googleErrorIdToken",
  state_invalid: "googleErrorState",
};

const HTTP_NOT_FOUND = 404;

export function GoogleFinishPage({
  search,
  navigate,
}: GoogleFinishPageProps = {}): JSX.Element {
  const [, T] = useT();
  const client = useApi();
  const { adopt } = useAuth();
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);
  // Guard against React 18 StrictMode's double-mount in dev — without
  // this the exchange code would be POSTed twice and the second call
  // returns 404 because exchange codes are single-use.
  const startedRef = useRef(false);

  function messageFor(code: string | null): string {
    const key = code !== null ? ERROR_KEYS_BY_CODE[code] : undefined;
    return T(key ?? "googleErrorGeneric");
  }

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const params = new URLSearchParams(search ?? window.location.search);
    const exchangeCode = params.get("exchange");
    const errorCode = params.get("error");

    if (errorCode !== null) {
      setError(messageFor(errorCode));
      setPhase("error");
      return;
    }

    if (exchangeCode === null) {
      setError(messageFor("state_invalid"));
      setPhase("error");
      return;
    }

    void (async () => {
      try {
        const response = await googleApi(client).exchange(exchangeCode);
        adopt(response);
        const goTo =
          navigate ?? ((url: string) => window.location.replace(url));
        goTo("/");
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === HTTP_NOT_FOUND) {
          setError(messageFor("state_invalid"));
        } else {
          setError(messageFor(null));
        }
        setPhase("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, adopt, search, navigate]);

  if (phase === "loading") {
    return (
      <AuthShell ariaLabel={T("finishingSignIn")}>
        <p role="status" aria-live="polite" style={{ margin: 0 }}>
          {T("finishingSignIn")}
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={T("appTitle")} ariaLabel={T("auth.signInError")}>
      <div className="vz-error" role="alert">
        {error}
      </div>
      <a
        href="/"
        className="vz-btn vz-btn-primary"
        style={{
          justifyContent: "center",
          padding: "10px",
          textDecoration: "none",
        }}
      >
        {T("backToLogin")}
      </a>
    </AuthShell>
  );
}
